/**
 * El reparto de los WhatsApp de un análisis entre las líneas por QR y WhatsApp oficial (la API), del lado de la
 * pantalla: leer lo que se tipeó, decir antes de guardar si no alcanza, y el resumen en palabras. La regla de a quién
 * le toca cada vía es del backend (`reparto.util.ts`); esto sólo evita mandar un pedido que se va a rechazar.
 */
import type { EstadoReparto } from "../types.real";
import { num, plural } from "./format";

/** Lo tipeado en un input de cantidad: vacío = 0; cualquier otra cosa que no sea un entero ≥ 0, null. */
export function leerCantidad(texto: string): number | null {
  const t = String(texto ?? "").trim();
  if (!t) return 0;
  if (!/^\d+$/.test(t)) return null;
  return Number(t);
}

/**
 * Por qué no se puede guardar este pedido, o null si se puede. Con los mismos números que va a mirar el backend
 * (`disponibles`): la API se llena primero con quienes pueden recibir por ella, y las líneas, con los que quedan.
 */
export function motivoNoAlcanza(qr: number | null, api: number | null, d: EstadoReparto["disponibles"] | null | undefined): string | null {
  if (qr === null || api === null) return "Poné números enteros, sin puntos ni comas.";
  if (qr === 0 && api === 0) return "Poné cuántos van por QR y cuántos por la API.";
  if (!d) return null;
  if (api > d.api) return `Por la API pueden recibir hasta ${num(d.api)}.`;
  const quedanQr = Math.min(d.qr, d.total - api);
  if (qr > quedanQr) {
    return api > 0
      ? `Por QR quedan hasta ${num(Math.max(0, quedanQr))} después de los ${num(api)} de la API.`
      : `Por QR pueden recibir hasta ${num(d.qr)}.`;
  }
  return null;
}

/**
 * Por qué no se ofrece «Por los dos», o null. Sin la cuenta de WhatsApp oficial, a los de la API no les escribiría
 * nadie (prueba del 16/10/2026). Mientras no se sabe (cargando, o un backend que no lo manda) se ofrece: el backend
 * rechaza igual el reparto (SIN_OFICIAL).
 */
export function motivoSinPorLosDos(alcance: { cuentaLista?: boolean } | null | undefined): string | null {
  return alcance?.cuentaLista === false
    ? "Conectá WhatsApp oficial para repartir: sin la cuenta, los de la API no recibirían por ninguna vía."
    : null;
}

/** «30 por QR y 20 por la API» */
export function resumenReparto(r: { qr: number; api: number }): string {
  const partes: string[] = [];
  if (r.qr > 0) partes.push(`${num(r.qr)} por QR`);
  if (r.api > 0) partes.push(`${num(r.api)} por la API`);
  return partes.join(" y ");
}

/** A dónde ir para seguir la parte de la API: el asistente si la campaña oficial es un borrador, si no su detalle. */
export function rutaParteApi(c: { id: string; estado: string } | null | undefined): string | null {
  if (!c?.id) return null;
  const id = encodeURIComponent(c.id);
  return c.estado === "borrador" ? `/activa/oficial/campanas/${id}/editar` : `/activa/oficial/campanas/${id}`;
}

/** «1 persona» / «20 personas» */
export const personas = (n: number) => `${num(n)} ${plural(n, "persona", "personas")}`;
